import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconInfoCircleOutline } from '@iwoca/icons';
import { GetDrawDownRepaymentScheduleCreditFacilityResponse } from '@iwoca/lapi-client/edge';
import { AlertBox } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import {
    AmortisationPhaseTable,
    AmotisationPhaseHeader
} from './AmortisationPhase';
import { PostRepaymentFreePeriodExplainer } from './PostRepaymentFreePeriodExplainer';
import {
    RepaymentFreePeriodHeader,
    RepaymentFreePeriodTable
} from './RepaymentFreePeriod';

export const RCFRepaymentSchedule = ({
    newOutstandingBalance,
    repaymentSchedule,
    isInRepaymentFreePeriod
}: {
    newOutstandingBalance: number;
    repaymentSchedule: GetDrawDownRepaymentScheduleCreditFacilityResponse['data'];
    isInRepaymentFreePeriod?: boolean;
}) => {
    const schedule = repaymentSchedule.schedule;
    const summary = repaymentSchedule.summary;

    const interestOnlySchedule = schedule.filter(
        (repayment) => repayment.principal === 0
    );

    const principalRepaymentsSchedule = schedule.filter(
        (repayment) => repayment.principal > 0
    );

    return (
        <div className="space-y-m">
            <div>
                <h3 className="mb-s mt-s text-2xl font-bold">
                    <FormattedMessage
                        id="_.rcf_approved_view.payment_plan_modal_title"
                        defaultMessage="Indicative payment plan"
                    />
                </h3>
                <Text data-testid="repayment-schedule-description">
                    <FormattedMessage
                        id="_.rcf_approved_view.repayment_schedule_description"
                        defaultMessage="*assuming your outstanding loan amount is {newOutstandingBalance}. The plan updates each time you withdraw or repay money."
                        values={{
                            newOutstandingBalance: (
                                <FormattedCurrency
                                    value={newOutstandingBalance}
                                    decimalPlaces={0}
                                />
                            )
                        }}
                    />
                </Text>
            </div>
            <div className="space-y-xl" data-testid="rcf-repayment-schedule">
                <div>
                    <RepaymentFreePeriodHeader
                        isInRepaymentFreePeriod={isInRepaymentFreePeriod}
                    />
                    <RepaymentFreePeriodTable schedule={interestOnlySchedule} />
                </div>
                <PostRepaymentFreePeriodExplainer />
                <div>
                    <AmotisationPhaseHeader />
                    <AmortisationPhaseTable
                        schedule={principalRepaymentsSchedule}
                        summary={summary}
                    />
                </div>
                <AlertBox variant="connect" className="flex flex-row gap-xs">
                    <IconInfoCircleOutline className="size-[20px] shrink-0" />
                    <Text className="text-s">
                        <FormattedMessage
                            id="_.rcf_approved_view.repayment_schedule_alert_box"
                            defaultMessage="<span>Remember:</span> you can make free repayments at any time and save on interest."
                            values={{
                                span: (chunks) => (
                                    <span className="font-bold">{chunks}</span>
                                )
                            }}
                        />
                    </Text>
                </AlertBox>
            </div>
        </div>
    );
};
